/**
 * Quantis Voice API: the office line's config, access tokens, call history,
 * voicemail and line settings. Shared by web and native, which each pass their
 * own API base and auth headers. Server side: routes/voice_routes.tsx.
 */
import { readError } from './_http';

export interface VoiceTransport {
  apiBase: string;
  authHeaders: (extra?: Record<string, string>) => Promise<Record<string, string>>;
}

/** A line as members see it. Departments carry no number: theirs is internal routing only. */
export interface VoiceLine { id: string; name: string; kind: 'main' | 'department' | 'direct'; number?: string }

export interface VoiceConfig {
  /** This workspace has Quantis Voice. */
  enabled: boolean;
  /** Its provider account and number are set up, so calls can be made. */
  connected?: boolean;
  /** The office number is still being moved to Quantis. */
  porting?: boolean;
  line?: VoiceLine;
  /** The lines this user may see. */
  lines?: VoiceLine[];
  /** Every department, by name, as menu options and transfer targets. */
  departments?: Array<{ id: string; name: string }>;
  identity?: string;
  canManage?: boolean;
}

export interface VoiceContactMatch { id: string; name: string; company: string | null }

export type CallOutcome = 'answered' | 'missed' | 'voicemail' | 'no_answer' | 'busy' | 'failed' | 'cancelled';

export interface VoiceCallRecord {
  id: string;
  line_id: string | null;
  /** The lines this call reached that the user can see, Main Line first. */
  line_names?: string[];
  direction: 'inbound' | 'outbound';
  other_number: string;
  status: string;
  outcome: CallOutcome | null;
  duration_sec: number | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  notes: string | null;
  contact: VoiceContactMatch | null;
  placed_by_name: string | null;
  answered_by_name: string | null;
}

export interface VoicemailRecord {
  id: string;
  call_log_id: string | null;
  line_id?: string | null;
  line_name?: string | null;
  from_number: string;
  duration_sec: number | null;
  transcript: string | null;
  transcript_status: 'pending' | 'completed' | 'failed' | 'none';
  heard_at: string | null;
  created_at: string;
  contact: VoiceContactMatch | null;
}

export type VoiceWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type VoiceBusinessHours = Partial<Record<VoiceWeekday, { open: string; close: string } | null>>;

export type UkVoice = 'Polly.Amy-Neural' | 'Polly.Emma-Neural' | 'Polly.Brian-Neural' | 'Polly.Arthur-Neural';

/** A line as the Phone lines page edits it. */
export interface VoiceLineDetail {
  id: string;
  kind: 'main' | 'department' | 'direct';
  name: string;
  owner_user_id: string | null;
  menu_digit: string | null;
  menu_prompt_text: string | null;
  announcement_text: string | null;
  ring_timeout_sec: number;
  /** Null: open 24 hours. */
  business_hours: VoiceBusinessHours | null;
  closed_dates: string[];
  closed_on_bank_holidays: boolean;
  greeting_text: string | null;
  menu_intro_text: string | null;
  menu_repeat_text: string | null;
  out_of_hours_text: string | null;
  voicemail_text: string | null;
  outbound_main_line_only: boolean;
  tts_voice: UkVoice;
  members: Array<{ user_id: string; can_answer: boolean; can_view_history: boolean; can_manage: boolean }>;
  /** Admins only, and internal: routing numbers are never shown anywhere else. */
  numbers: Array<{ number: string; status: 'active' | 'porting'; public: boolean; label: string | null }>;
}

export interface VoiceLinesResponse {
  lines: VoiceLineDetail[];
  people: Array<{ id: string; name: string }>;
  voices: UkVoice[];
  isAdmin: boolean;
}

export type VoiceLinePatch = Partial<Omit<VoiceLineDetail, 'id' | 'kind' | 'owner_user_id' | 'numbers'>>;

async function request<T>(transport: VoiceTransport, path: string, init: RequestInit = {}): Promise<T> {
  const headers = await transport.authHeaders(init.body ? { 'Content-Type': 'application/json' } : {});
  const res = await fetch(`${transport.apiBase}/voice${path}`, { ...init, headers });
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<T>;
}

export interface VoiceApi {
  config: () => Promise<VoiceConfig>;
  token: () => Promise<{ token: string; identity: string; expiresIn: number }>;
  /** Recent calls on the lines the user may see, or on one of them. */
  calls: (line?: string | null) => Promise<VoiceCallRecord[]>;
  saveNotes: (callId: string, notes: string) => Promise<void>;
  voicemails: (line?: string | null) => Promise<VoicemailRecord[]>;
  markHeard: (voicemailId: string) => Promise<void>;
  voicemailAudioUrl: (voicemailId: string) => Promise<string>;
  lines: () => Promise<VoiceLinesResponse>;
  saveLine: (id: string, patch: VoiceLinePatch) => Promise<void>;
  addDepartment: (name: string) => Promise<void>;
  /** Admins name the office number before it arrives. */
  setUpMainLine: (number: string) => Promise<void>;
}

export function createVoiceApi(transport: VoiceTransport): VoiceApi {
  return {
    config: async () => {
      try {
        return await request<VoiceConfig>(transport, '/config');
      } catch {
        // No line, no secrets, or a server too old to know the route: the
        // feature is simply absent rather than an error on every screen.
        // A native Debug build previews Calls, unconnected, until the routes are deployed; release builds never do.
        return (globalThis as { __DEV__?: boolean }).__DEV__ === true ? { enabled: true, connected: false } : { enabled: false };
      }
    },
    token: () => request(transport, '/token', { method: 'POST' }),
    calls: async (line) => {
      const query = line ? `?line=${encodeURIComponent(line)}` : '';
      const body = await request<{ calls?: VoiceCallRecord[] }>(transport, `/calls${query}`);
      return Array.isArray(body.calls) ? body.calls : [];
    },
    saveNotes: async (callId, notes) => {
      await request(transport, `/calls/${encodeURIComponent(callId)}`, { method: 'PATCH', body: JSON.stringify({ notes }) });
    },
    voicemails: async (line) => {
      const body = await request<{ voicemails?: VoicemailRecord[] }>(transport, `/voicemails${line ? `?line=${encodeURIComponent(line)}` : ''}`);
      return Array.isArray(body.voicemails) ? body.voicemails : [];
    },
    markHeard: async (id) => {
      await request(transport, `/voicemails/${encodeURIComponent(id)}/heard`, { method: 'POST' });
    },
    voicemailAudioUrl: async (id) => (await request<{ url: string }>(transport, `/voicemails/${encodeURIComponent(id)}/audio`)).url,
    lines: () => request(transport, '/lines'),
    saveLine: async (id, patch) => {
      await request(transport, `/lines/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) });
    },
    addDepartment: async (name) => {
      await request(transport, '/lines', { method: 'POST', body: JSON.stringify({ name }) });
    },
    setUpMainLine: async (number) => {
      await request(transport, '/main-line', { method: 'POST', body: JSON.stringify({ number }) });
    },
  };
}
